import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, Instagram, Facebook, Linkedin, ArrowRight, Lock, Briefcase, Share2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/landing/Header";
import { openEnquiry } from "@/lib/api";
import { CONTACT, FAQS, IMAGES } from "@/data/siteData";

export function FAQ() {
    return (
        <section id="faq" className="py-20 bg-white border-b border-stone-300/70" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">sawaal jawab</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Frequently Asked Questions</h2>

                <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`faq-${i}`} className="border-b border-stone-200">
                            <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-display font-bold text-base text-navy hover:text-saffron hover:no-underline py-5">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm text-stone-600 leading-relaxed pb-5">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

export function FinalCTA() {
    return (
        <section id="contact" className="relative py-28 overflow-hidden" data-testid="final-cta-section">
            <img src={IMAGES.coupleTravel} alt="Travellers watching a mountain sunset" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-navy-deep/85" />
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                <p className="font-script text-4xl sm:text-5xl text-gold">kyunki zindagi ek safar hai</p>
                <h2 className="mt-2 font-display font-black text-4xl sm:text-6xl tracking-tight" data-testid="final-cta-headline">THE WORLD IS WAITING.</h2>
                <p className="mt-4 text-white/70 text-base sm:text-lg">Stop planning the trip. Start planning the memories.</p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <button onClick={() => openEnquiry()} data-testid="final-plan-my-trip-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-display font-black uppercase tracking-wider text-white bg-saffron hover:bg-saffron-dark transition-all hover:-translate-y-0.5">
                        Plan My Trip <ArrowRight className="h-4 w-4" />
                    </button>
                    <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} data-testid="final-talk-to-expert-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-display font-black uppercase tracking-wider text-white border-2 border-white/60 hover:bg-white hover:text-navy transition-all">
                        <Phone className="h-4 w-4" /> Talk to a Trip Expert
                    </a>
                </div>
            </div>
        </section>
    );
}

const FOOTER_LINKS = {
    Company: [
        { label: "About Us", to: "/#about" },
        { label: "Contact", to: "/contact" },
        { label: "Associates & Partners", to: "/partners" },
    ],
    Trips: [
        { label: "School Trips", to: "/school-trips" },
        { label: "College Trips", to: "/college-trips" },
        { label: "Corporate Trips", to: "/corporate-trips" },
        { label: "Industrial Visits", to: "/industrial-visits" },
        { label: "Group Tours", to: "/group-tours" },
    ],
    Destinations: [
        { label: "India", to: "/destinations" },
        { label: "International", to: "/destinations" },
    ],
    Support: [
        { label: "Contact Us", to: "/contact" },
        { label: "FAQs", to: "/#faq" },
        { label: "Terms & Conditions", to: "/terms" },
        { label: "Privacy Policy", to: "/privacy-policy" },
    ],
};

export function Footer() {
    return (
        <footer className="bg-navy-deep text-white/70" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
                    <div className="col-span-2">
                        <Logo light />
                        <p className="mt-4 text-sm leading-relaxed">
                            Experiences, trips and journeys planned for people who love to explore.
                        </p>
                        <p className="mt-2 text-xs text-white/40">A brand of <a href="https://www.bigkreation.com" target="_blank" rel="noreferrer" data-testid="footer-parent-link" className="underline decoration-white/30 underline-offset-2 hover:text-gold hover:decoration-gold transition-colors">BigKreation Entertainment Pvt. Ltd.</a></p>
                        <div className="mt-5 flex gap-3">
                            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-saffron hover:border-saffron transition-colors"><Instagram className="h-4 w-4" /></a>
                            <a href={CONTACT.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-saffron hover:border-saffron transition-colors"><Facebook className="h-4 w-4" /></a>
                            <a href="https://linkedin.com/company/ghumnechaley" target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-saffron hover:border-saffron transition-colors"><Linkedin className="h-4 w-4" /></a>
                        </div>
                    </div>
                    {Object.entries(FOOTER_LINKS).map(([title, links]) => (
                        <div key={title}>
                            <h3 className="font-display font-black text-xs uppercase tracking-widest text-white">{title}</h3>
                            <ul className="mt-4 space-y-2.5 text-sm">
                                {links.map((l) => (
                                    <li key={l.label}>
                                        <Link to={l.to} className="hover:text-gold transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-12 rounded-2xl bg-saffron p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8 shadow-[8px_8px_0_0_rgba(0,0,0,0.25)]" data-testid="footer-hiring-banner">
                    <div className="flex items-center gap-4 flex-1">
                        <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15">
                            <Briefcase className="h-5 w-5 text-white" />
                            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-gold animate-ping" />
                            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-gold" />
                        </span>
                        <div>
                            <p className="font-display font-black text-xl sm:text-2xl text-white tracking-tight">GC is Hiring!</p>
                            <p className="mt-0.5 text-sm text-white/85">Love travel? Work where every week is a new destination — apply in 60 seconds, or refer a friend who'd be perfect.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3 shrink-0">
                        <Link to="/careers" data-testid="footer-hiring-apply-btn" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-display font-black uppercase tracking-wider text-navy hover:bg-navy hover:text-white transition-colors">
                            <Briefcase className="h-3.5 w-3.5" /> Apply Now
                        </Link>
                        <a
                            href={`https://wa.me/?text=${encodeURIComponent("GhumneChaley is hiring! Travel roles & internships open — apply in 60 seconds, no login needed: https://ghumnechaley.com/careers")}`}
                            target="_blank" rel="noreferrer"
                            data-testid="footer-hiring-refer-btn"
                            className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-6 py-3 text-xs font-display font-black uppercase tracking-wider text-white hover:bg-white hover:text-saffron transition-colors"
                        >
                            <Share2 className="h-3.5 w-3.5" /> Refer a Friend
                        </a>
                    </div>
                </div>

                <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm" data-testid="footer-contact">
                    <div className="flex items-start gap-3">
                        <Phone className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                        <div>
                            <div className="text-white font-bold text-xs uppercase tracking-widest mb-1">Call Us</div>
                            <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} className="block hover:text-gold">{CONTACT.phonePrimary}</a>
                            <a href={`tel:${CONTACT.phoneSecondary.replace(/\s/g, "")}`} className="block hover:text-gold">{CONTACT.phoneSecondary}</a>
                            <a href={`tel:${CONTACT.phoneTertiary.replace(/\s/g, "")}`} className="block hover:text-gold">{CONTACT.phoneTertiary}</a>
                            <span className="block text-white/50">Tel: {CONTACT.landline}</span>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <MessageCircle className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                        <div>
                            <div className="text-white font-bold text-xs uppercase tracking-widest mb-1">WhatsApp & Email</div>
                            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="block hover:text-gold">{CONTACT.phonePrimary}</a>
                            <a href={`mailto:${CONTACT.email}`} className="block hover:text-gold">{CONTACT.email}</a>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Mail className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                        <div>
                            <div className="text-white font-bold text-xs uppercase tracking-widest mb-1">Visit Us</div>
                            <p className="leading-relaxed">{CONTACT.address}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/40">
                    <span data-testid="footer-copyright">© 2026 GhumneChaley.com. All Rights Reserved.</span>
                    <span className="flex items-center gap-4">
                        <span>Sirf Trip Nahi. Yaadein Banane Chale.</span>
                        <Link to="/admin" data-testid="footer-admin-login" className="inline-flex items-center gap-1.5 text-white/50 hover:text-gold transition-colors font-bold uppercase tracking-wider text-[10px]">
                            <Lock className="h-3 w-3" /> Admin Login
                        </Link>
                    </span>
                </div>
            </div>
        </footer>
    );
}
